import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, Trash, PlusCircle, MagnifyingGlass, Sparkle, Camera, UploadSimple, X as XIcon, BookOpen, MagicWand, ShieldCheck } from "@phosphor-icons/react";
import SnippetPreview from "@/components/SnippetPreview";
import useAiJob from "@/hooks/useAiJob";

// Convert form-input value → int|null so blank fields wipe rather than saving 0.
const numOrNull = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * HistoricalPanel — Admin curation UI for public-domain recipes.
 *
 * Actions:
 *   • Seed initial 50+ verified recipes (idempotent).
 *   • AI batch-generate 50 more real historical recipes (Claude drafts;
 *     each entry lands unpublished + unverified for admin review).
 *   • Search / filter existing entries.
 *   • Toggle published + verified on each row.
 *   • Per-row inspector modal — upload/generate/delete photo, AI-draft the
 *     history summary + review cited sources, flip `history_verified`.
 *   • Delete individual entries.
 *   • CSV export of the full ledger.
 */
export default function HistoricalPanel() {
  const [state, setState] = useState({ items: [], total: 0, loading: true });
  const [q, setQ] = useState("");
  const [pubFilter, setPubFilter] = useState("all");
  const [verFilter, setVerFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [newRow, setNewRow] = useState({ title: "", source_book: "", author: "", year: 1861, country: "", continent: "Europe", century: 19, decade: 1860, era_label: "" });
  const [inspecting, setInspecting] = useState(null); // full recipe doc when the inspector is open
  const [inspectorBusy, setInspectorBusy] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);
  const uploadRef = useRef(null);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/admin/historical/recipes", { params: { q, published: pubFilter, verified: verFilter, limit: 200 } });
      setState({ items: data.items, total: data.total, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
      setState({ items: [], total: 0, loading: false });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [pubFilter, verFilter]);

  const seed = async () => {
    if (!window.confirm("Seed the initial curated batch of pre-1900 historical recipes? Idempotent — existing titles are skipped.")) return;
    try {
      const { data } = await api.post("/admin/historical/seed");
      toast.success(`Seeded ${data.inserted} new (skipped ${data.skipped}/${data.seed_size})`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); }
  };

  const toggleField = async (id, field, value) => {
    try {
      await api.patch(`/admin/historical/recipes/${id}`, { [field]: value });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/historical/recipes/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const create = async () => {
    if (!newRow.title || !newRow.source_book || !newRow.author) { toast.error("Title, source book, author required"); return; }
    try {
      await api.post("/admin/historical/recipes", { ...newRow, ingredients: [], instructions: [] });
      toast.success("Recipe created — edit ingredients/instructions from row view");
      setShowAdd(false);
      setNewRow({ ...newRow, title: "", source_book: "", author: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/historical/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `historical-recipes-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const generateJob = useAiJob({
    statusUrl: (id) => `/admin/historical/generate-batch/status/${id}`,
    onDone: (job) => {
      const inserted = job.inserted ?? 0;
      const skipped = job.skipped ?? 0;
      toast.success(`AI drafted ${inserted} new recipes (skipped ${skipped}). Review + publish each one from the table.`);
      setPubFilter("no");
      setBatchBusy(false);
      load();
    },
    onCancelled: (job) => {
      const inserted = job.inserted ?? 0;
      const dropped = (job.total ?? 0) - (job.processed ?? 0);
      toast(`Cancelled — kept ${inserted} drafts already saved, dropped remaining ${dropped} batch${dropped === 1 ? "" : "es"}.`);
      if (inserted > 0) setPubFilter("no");
      setBatchBusy(false);
      load();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || e?.message || "Batch generate failed");
      setBatchBusy(false);
    },
  });

  const generateBatch = async () => {
    if (!window.confirm("Ask the AI to draft 50 more real, pre-1900 historical recipes? They'll land unpublished + unverified so you can review each one before publishing.")) return;
    setBatchBusy(true);
    try {
      await generateJob.start(async () => {
        const { data } = await api.post("/admin/historical/generate-batch", { count: 50 });
        return data;
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch generate failed");
      setBatchBusy(false);
    }
  };

  const openInspector = async (id) => {
    setInspectorBusy(true);
    try {
      // `?fresh=1` bypasses the service-worker recipe cache — otherwise
      // reopening the inspector immediately after saving SEO overrides
      // would return the STALE cached copy (no SEO fields visible).
      const { data } = await api.get(`/historical/recipes/${id}?fresh=1`);
      // Hydrate a scratch `_tagsRaw` so the Tags text-input can be edited
      // as a single comma-separated string without splitting on every keypress.
      setInspecting({ ...data, _tagsRaw: (data.tags || []).join(", ") });
    } catch (e) {
      toast.error("Couldn't load recipe");
    } finally { setInspectorBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!inspecting || !file) return;
    setInspectorBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/historical/recipes/${inspecting.historical_id}/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInspecting({ ...inspecting, image_url: data.image_url });
      toast.success("Photo uploaded");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setInspectorBusy(false); }
  };

  const generateAiImage = async () => {
    if (!inspecting) return;
    setInspectorBusy(true);
    try {
      const { data } = await api.post(`/admin/historical/recipes/${inspecting.historical_id}/ai-image`);
      setInspecting({ ...inspecting, image_url: data.image_url });
      toast.success("AI photo generated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed — SuperAdmin only");
    } finally { setInspectorBusy(false); }
  };

  const deletePhoto = async () => {
    if (!inspecting || !window.confirm("Remove the current photo?")) return;
    setInspectorBusy(true);
    try {
      await api.delete(`/admin/historical/recipes/${inspecting.historical_id}/image`);
      setInspecting({ ...inspecting, image_url: null });
      toast.success("Photo removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setInspectorBusy(false); }
  };

  const draftHistory = async () => {
    if (!inspecting) return;
    setInspectorBusy(true);
    try {
      const { data } = await api.post(`/admin/historical/recipes/${inspecting.historical_id}/ai-history`);
      setInspecting({ ...inspecting, history_summary: data.summary, history_sources: data.sources || [], history_verified: false });
      toast.success("AI drafted the history summary. Review each source below, edit if needed, then flip 'Verified' before it goes live.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI history draft failed");
    } finally { setInspectorBusy(false); }
  };

  const saveHistoryEdits = async () => {
    if (!inspecting) return;
    setInspectorBusy(true);
    try {
      await api.patch(`/admin/historical/recipes/${inspecting.historical_id}`, {
        history_summary: inspecting.history_summary || "",
        history_sources: inspecting.history_sources || [],
        history_verified: !!inspecting.history_verified,
      });
      toast.success("History saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setInspectorBusy(false); }
  };

  /* Admin-only SEO overrides — used by /historical/{slug} page's PageMeta
   * to shape the <title>, meta description and hero <img alt>. Empty string
   * clears the override and reverts to the auto-generated defaults. */
  const saveSeoEdits = async () => {
    if (!inspecting) return;
    setInspectorBusy(true);
    try {
      await api.patch(`/admin/historical/recipes/${inspecting.historical_id}`, {
        seo_title: inspecting.seo_title || "",
        seo_description: inspecting.seo_description || "",
        seo_keywords: inspecting.seo_keywords || "",
        image_alt: inspecting.image_alt || "",
      });
      toast.success("SEO overrides saved — live on next page load");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setInspectorBusy(false); }
  };

  /* Recipe body edits — SuperAdmin can correct anything the AI got wrong on
   * a Historical draft: description, servings, timings, ingredients,
   * instructions, difficulty, cuisine tags and the verbatim original_note. */
  const saveBodyEdits = async () => {
    if (!inspecting) return;
    setInspectorBusy(true);
    try {
      const patch = {
        description: inspecting.description || "",
        serves: inspecting.serves || null,
        prep_minutes: numOrNull(inspecting.prep_minutes),
        cook_minutes: numOrNull(inspecting.cook_minutes),
        rest_minutes: numOrNull(inspecting.rest_minutes),
        difficulty: inspecting.difficulty || null,
        ingredients: (inspecting.ingredients || []).map((s) => (s || "").trim()).filter(Boolean),
        instructions: (inspecting.instructions || []).map((s) => (s || "").trim()).filter(Boolean),
        tags: typeof inspecting._tagsRaw === "string"
          ? inspecting._tagsRaw.split(",").map((t) => t.trim()).filter(Boolean)
          : (inspecting.tags || []),
        original_note: inspecting.original_note || null,
        era_label: inspecting.era_label || "",
      };
      const { data } = await api.patch(`/admin/historical/recipes/${inspecting.historical_id}`, patch);
      // Re-sync from server so any server-side normalisation (slug, updated_at) is reflected
      setInspecting({ ...data, _tagsRaw: (data.tags || []).join(", ") });
      toast.success("Recipe body saved — live on the public page immediately");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setInspectorBusy(false); }
  };

  /* Ask Claude to draft optimised SEO fields — populates the inputs so the
   * admin can review before saving. Non-destructive: only fills fields
   * the LLM returned non-empty for. */
  const suggestSeoEdits = async () => {
    if (!inspecting) return;
    setInspectorBusy(true);
    try {
      const { data } = await api.post(`/admin/historical/recipes/${inspecting.historical_id}/suggest-seo`);
      if (!data || (!data.seo_title && !data.seo_description && !data.image_alt && !data.seo_keywords)) {
        toast.error("AI didn't return a suggestion — try again in a moment");
        return;
      }
      setInspecting((prev) => ({
        ...prev,
        seo_title: data.seo_title || prev.seo_title,
        seo_description: data.seo_description || prev.seo_description,
        seo_keywords: data.seo_keywords || prev.seo_keywords,
        image_alt: data.image_alt || prev.image_alt,
      }));
      toast.success("AI suggestions loaded — review and save");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally { setInspectorBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display font-black text-2xl text-rf-ink">Historical Recipes</h2>
          <p className="text-sm text-rf-ink-soft mt-1">{state.total} in library · pre-1900 public-domain recipes</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="historical-seed-btn" onClick={seed} className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-2">
            <Sparkle size={16} weight="bold" /> Seed curated batch
          </button>
          <button data-testid="historical-batch-generate" onClick={generateBatch} disabled={batchBusy} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            <MagicWand size={16} weight="bold" /> {batchBusy ? "AI drafting…" : "AI-generate 50 more"}
          </button>
          <button data-testid="historical-add-btn" onClick={() => setShowAdd((v) => !v)} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-2">
            <PlusCircle size={16} weight="bold" /> Add new
          </button>
          <button data-testid="historical-export-csv" onClick={exportCsv} className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-2">
            <DownloadSimple size={16} weight="bold" /> CSV
          </button>
        </div>
      </div>

      {generateJob.progress && generateJob.progress.total > 0 && (
        <div className="bg-white rounded-2xl border border-rf-ink/10 p-4 mb-4" data-testid="historical-batch-progress">
          <div className="flex items-center justify-between text-xs font-bold text-rf-ink-soft mb-1">
            <span>
              {generateJob.progress.status === "done"
                ? "Done"
                : generateJob.progress.status === "cancelled"
                ? "Cancelled"
                : `Batch ${generateJob.progress.processed} / ${generateJob.progress.total} — ${generateJob.progress.inserted} drafted, ${generateJob.progress.skipped} skipped`}
            </span>
            <div className="flex items-center gap-3">
              <span>
                {Math.round(((generateJob.progress.processed || 0) / (generateJob.progress.total || 1)) * 100)}%
              </span>
              {generateJob.progress.status === "running" && !generateJob.progress.cancel_requested && (
                <button
                  type="button"
                  onClick={generateJob.cancel}
                  data-testid="historical-batch-cancel"
                  className="text-rf-ink-soft hover:text-red-600 underline font-semibold"
                >
                  Cancel
                </button>
              )}
              {generateJob.progress.cancel_requested && generateJob.progress.status === "running" && (
                <span className="text-amber-600 font-semibold">Cancelling…</span>
              )}
            </div>
          </div>
          <div className="w-full h-2 rounded-full bg-rf-ink/10 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                generateJob.progress.status === "cancelled" ? "bg-rf-ink-soft" : "bg-rf-orange"
              }`}
              style={{ width: `${Math.min(100, Math.round(((generateJob.progress.processed || 0) / (generateJob.progress.total || 1)) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {showAdd && (
        <div className="bg-white rounded-3xl border border-rf-ink/10 p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="historical-add-form">
          <input className="input-rf" placeholder="Title *" value={newRow.title} onChange={(e) => setNewRow({ ...newRow, title: e.target.value })} />
          <input className="input-rf" placeholder="Source book *" value={newRow.source_book} onChange={(e) => setNewRow({ ...newRow, source_book: e.target.value })} />
          <input className="input-rf" placeholder="Author *" value={newRow.author} onChange={(e) => setNewRow({ ...newRow, author: e.target.value })} />
          <input className="input-rf" type="number" max={1900} placeholder="Year (≤1900)" value={newRow.year} onChange={(e) => setNewRow({ ...newRow, year: parseInt(e.target.value) || 1861 })} />
          <input className="input-rf" placeholder="Country" value={newRow.country} onChange={(e) => setNewRow({ ...newRow, country: e.target.value })} />
          <input className="input-rf" placeholder="Continent" value={newRow.continent} onChange={(e) => setNewRow({ ...newRow, continent: e.target.value })} />
          <input className="input-rf" type="number" placeholder="Century" value={newRow.century} onChange={(e) => setNewRow({ ...newRow, century: parseInt(e.target.value) || 19 })} />
          <input className="input-rf" placeholder="Era label" value={newRow.era_label} onChange={(e) => setNewRow({ ...newRow, era_label: e.target.value })} />
          <button onClick={create} className="btn-amber col-span-2 md:col-span-4 text-sm">Create (ingredients/steps added via row edit)</button>
        </div>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-rf-ink-soft" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search title/book/author/country" className="input-rf w-full !pl-9" data-testid="historical-search-admin" />
        </div>
        <select value={pubFilter} onChange={(e) => setPubFilter(e.target.value)} className="input-rf max-w-[180px]">
          <option value="all">All published states</option><option value="yes">Published only</option><option value="no">Hidden only</option>
        </select>
        <select value={verFilter} onChange={(e) => setVerFilter(e.target.value)} className="input-rf max-w-[180px]">
          <option value="all">All verification</option><option value="yes">Verified only</option><option value="no">Unverified only</option>
        </select>
        <button onClick={load} className="btn-outline-ink !py-2 !px-4 text-sm">Search</button>
      </div>

      {state.loading ? <p className="text-center text-rf-ink-soft py-8">Loading…</p> : state.items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-rf-ink/5">
          <p className="text-rf-ink-soft">No historical recipes yet. Click <b>Seed curated batch</b> to add the initial 50+.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-rf-ink/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="historical-admin-table">
              <thead>
                <tr className="text-left text-[11px] font-black uppercase tracking-widest text-rf-ink-soft bg-rf-cream-alt">
                  <th className="px-4 py-3">Title</th><th className="px-4 py-3">Source · Year</th><th className="px-4 py-3">Country</th>
                  <th className="px-4 py-3">Pub.</th><th className="px-4 py-3">Verified</th><th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((r) => (
                  <tr key={r.historical_id} className="border-t border-rf-ink/5">
                    <td className="px-4 py-3 font-bold text-rf-ink truncate max-w-[280px]">
                      <a href={`/historical/${r.slug || r.historical_id}`} target="_blank" rel="noreferrer" className="hover:underline">{r.title}</a>
                    </td>
                    <td className="px-4 py-3 text-rf-ink-soft text-xs">{r.source_book} · {r.year > 0 ? r.year : `${Math.abs(r.year)} BC`}</td>
                    <td className="px-4 py-3 text-rf-ink-soft">{r.country}</td>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={!!r.published} onChange={(e) => toggleField(r.historical_id, "published", e.target.checked)} className="w-4 h-4 accent-rf-orange" />
                    </td>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={!!r.source_verified} onChange={(e) => toggleField(r.historical_id, "source_verified", e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openInspector(r.historical_id)}
                          className="text-rf-orange hover:text-rf-ink"
                          data-testid={`historical-inspect-${r.historical_id}`}
                          title="Inspect / photo / history"
                        >
                          <BookOpen size={16} weight="duotone" />
                        </button>
                        <button onClick={() => del(r.historical_id, r.title)} className="text-red-600 hover:text-red-800" data-testid={`historical-delete-${r.historical_id}`}>
                          <Trash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Inspector modal — photo + history summary curation */}
      {inspecting && (
        <div className="fixed inset-0 z-50 bg-rf-ink/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="historical-inspector">
          <div className="bg-white rounded-3xl max-w-3xl w-full my-8 shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-rf-ink/10">
              <div>
                <p className="overline">Inspector</p>
                <h3 className="font-display font-black text-xl text-rf-ink">{inspecting.title}</h3>
                <p className="text-xs text-rf-ink-soft">{inspecting.source_book} · {inspecting.author} · {inspecting.year}</p>
              </div>
              <button type="button" onClick={() => setInspecting(null)} className="p-2 hover:bg-rf-cream-alt rounded-full" data-testid="historical-inspector-close">
                <XIcon size={18} weight="bold" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* PHOTO */}
              <section>
                <p className="overline mb-2">Headline photo</p>
                <div className="flex gap-4 items-start">
                  <div className="w-40 aspect-[4/3] rounded-2xl overflow-hidden bg-rf-cream-alt flex items-center justify-center flex-shrink-0">
                    {inspecting.image_url ? (
                      <img src={inspecting.image_url} alt={inspecting.title} className="w-full h-full object-cover" />
                    ) : (
                      <Camera size={32} weight="duotone" className="text-rf-ink-soft" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={uploadRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="historical-inspector-upload-input"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }}
                    />
                    <button
                      type="button"
                      onClick={() => uploadRef.current?.click()}
                      disabled={inspectorBusy}
                      className="btn-outline-ink w-full inline-flex items-center gap-2 !py-2 !px-3 text-sm disabled:opacity-60"
                      data-testid="historical-inspector-upload"
                    >
                      <UploadSimple size={16} weight="bold" /> Upload a photo
                    </button>
                    <button
                      type="button"
                      onClick={generateAiImage}
                      disabled={inspectorBusy}
                      className="btn-amber w-full inline-flex items-center gap-2 !py-2 !px-3 text-sm disabled:opacity-60"
                      data-testid="historical-inspector-ai-image"
                    >
                      <Sparkle size={16} weight="bold" /> AI-generate photo (SuperAdmin)
                    </button>
                    {inspecting.image_url && (
                      <button
                        type="button"
                        onClick={deletePhoto}
                        disabled={inspectorBusy}
                        className="btn-outline-ink w-full inline-flex items-center gap-2 !py-2 !px-3 text-sm !text-red-700 !border-red-300 hover:!bg-red-50 disabled:opacity-60"
                        data-testid="historical-inspector-delete-image"
                      >
                        <Trash size={16} weight="bold" /> Remove photo
                      </button>
                    )}
                  </div>
                </div>
              </section>

              {/* RECIPE BODY — SuperAdmin can correct AI mistakes on any
                  field: description, servings, timings, ingredients,
                  instructions, difficulty, cuisine tags. */}
              <section data-testid="historical-body-section">
                <p className="overline mb-2">Recipe body (correct AI mistakes)</p>
                <p className="text-xs text-rf-ink-soft mb-3">
                  Fixed here → live on the public /historical/{"{"}slug{"}"} page immediately after save.
                </p>

                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Description</label>
                <textarea
                  rows={3}
                  className="input-rf w-full text-sm mb-3"
                  placeholder="Short intro shown above the ingredients — 1-3 sentences."
                  value={inspecting.description || ""}
                  onChange={(e) => setInspecting({ ...inspecting, description: e.target.value })}
                  data-testid="historical-body-description"
                />

                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Serves</label>
                    <input
                      className="input-rf w-full text-sm"
                      placeholder="e.g. 4"
                      value={inspecting.serves || ""}
                      onChange={(e) => setInspecting({ ...inspecting, serves: e.target.value })}
                      data-testid="historical-body-serves"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Prep (min)</label>
                    <input
                      type="number"
                      min={0}
                      className="input-rf w-full text-sm"
                      value={inspecting.prep_minutes ?? ""}
                      onChange={(e) => setInspecting({ ...inspecting, prep_minutes: e.target.value })}
                      data-testid="historical-body-prep"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Cook (min)</label>
                    <input
                      type="number"
                      min={0}
                      className="input-rf w-full text-sm"
                      value={inspecting.cook_minutes ?? ""}
                      onChange={(e) => setInspecting({ ...inspecting, cook_minutes: e.target.value })}
                      data-testid="historical-body-cook"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Stand / rest (min)</label>
                    <input
                      type="number"
                      min={0}
                      className="input-rf w-full text-sm"
                      value={inspecting.rest_minutes ?? ""}
                      onChange={(e) => setInspecting({ ...inspecting, rest_minutes: e.target.value })}
                      data-testid="historical-body-rest"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Difficulty</label>
                    <select
                      className="input-rf w-full text-sm"
                      value={inspecting.difficulty || ""}
                      onChange={(e) => setInspecting({ ...inspecting, difficulty: e.target.value })}
                      data-testid="historical-body-difficulty"
                    >
                      <option value="">—</option>
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>
                </div>

                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Era label</label>
                <input
                  className="input-rf w-full text-sm mb-3"
                  placeholder='e.g. "Victorian", "Ming Dynasty", "Ancient Rome"'
                  value={inspecting.era_label || ""}
                  onChange={(e) => setInspecting({ ...inspecting, era_label: e.target.value })}
                  data-testid="historical-body-era"
                />

                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Ingredients</label>
                <div className="space-y-1 mb-3">
                  {(inspecting.ingredients || []).map((ing, i) => (
                    <div key={i} className="flex gap-1">
                      <input
                        className="input-rf flex-1 text-sm !py-2"
                        value={ing}
                        onChange={(e) => {
                          const next = [...(inspecting.ingredients || [])];
                          next[i] = e.target.value;
                          setInspecting({ ...inspecting, ingredients: next });
                        }}
                        data-testid={`historical-body-ingredient-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = (inspecting.ingredients || []).filter((_, j) => j !== i);
                          setInspecting({ ...inspecting, ingredients: next });
                        }}
                        className="px-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remove ingredient"
                      >
                        <XIcon size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setInspecting({ ...inspecting, ingredients: [...(inspecting.ingredients || []), ""] })}
                    className="text-xs text-rf-orange font-bold hover:underline"
                    data-testid="historical-body-add-ingredient"
                  >+ Add an ingredient</button>
                </div>

                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Instructions</label>
                <div className="space-y-1 mb-3">
                  {(inspecting.instructions || []).map((step, i) => (
                    <div key={i} className="flex gap-1">
                      <span className="text-xs font-bold text-rf-orange w-6 pt-2 text-center flex-shrink-0">{i + 1}</span>
                      <textarea
                        rows={2}
                        className="input-rf flex-1 text-sm !py-2"
                        value={step}
                        onChange={(e) => {
                          const next = [...(inspecting.instructions || [])];
                          next[i] = e.target.value;
                          setInspecting({ ...inspecting, instructions: next });
                        }}
                        data-testid={`historical-body-instruction-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = (inspecting.instructions || []).filter((_, j) => j !== i);
                          setInspecting({ ...inspecting, instructions: next });
                        }}
                        className="px-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remove step"
                      >
                        <XIcon size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setInspecting({ ...inspecting, instructions: [...(inspecting.instructions || []), ""] })}
                    className="text-xs text-rf-orange font-bold hover:underline"
                    data-testid="historical-body-add-instruction"
                  >+ Add a step</button>
                </div>

                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Tags (comma-separated)</label>
                <input
                  className="input-rf w-full text-sm mb-3"
                  placeholder="e.g. Mesopotamian, lamb, stew, ancient"
                  value={inspecting._tagsRaw ?? (inspecting.tags || []).join(", ")}
                  onChange={(e) => setInspecting({ ...inspecting, _tagsRaw: e.target.value })}
                  data-testid="historical-body-tags"
                />

                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Original note (verbatim quirky bit from the source)</label>
                <textarea
                  rows={2}
                  className="input-rf w-full text-sm mb-3"
                  placeholder='e.g. "Serve very hot with a good stout ale, as was the fashion."'
                  value={inspecting.original_note || ""}
                  onChange={(e) => setInspecting({ ...inspecting, original_note: e.target.value })}
                  data-testid="historical-body-original-note"
                />

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={saveBodyEdits}
                    disabled={inspectorBusy}
                    className="btn-amber inline-flex items-center gap-2 text-sm disabled:opacity-60"
                    data-testid="historical-body-save"
                  >
                    <ShieldCheck size={16} weight="bold" /> Save recipe body
                  </button>
                </div>
              </section>

              {/* SEO OVERRIDES — Admin-only shortcuts to override the
                  auto-generated <title>, meta description, and hero image
                  alt on the public /historical/{slug} page. Leaving a field
                  blank keeps the default (title / cleaned description). */}
              <section data-testid="historical-seo-section">
                <p className="overline mb-2">SEO / accessibility overrides</p>
                <p className="text-xs text-rf-ink-soft mb-3">
                  Live on the public page immediately after save. Leave any field blank to use the auto-generated default. Google and Bing normally re-crawl within a few days.
                </p>
                {/* AI drafter — one click populates all three fields with a
                    Claude-generated suggestion. Admin still reviews + saves. */}
                <button
                  type="button"
                  onClick={suggestSeoEdits}
                  disabled={inspectorBusy}
                  data-testid="historical-seo-suggest"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-rf-orange/40 hover:border-rf-orange text-rf-orange hover:bg-rf-orange/5 font-black text-sm py-2.5 mb-4 transition-colors disabled:opacity-60"
                >
                  <Sparkle size={16} weight="fill" /> Suggest with AI
                </button>
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">SEO title (page &lt;title&gt;)</label>
                <input
                  type="text"
                  className="input-rf w-full mb-3"
                  placeholder={`e.g. ${inspecting.title} — Traditional ${inspecting.country} recipe (${inspecting.year})`}
                  maxLength={110}
                  value={inspecting.seo_title || ""}
                  onChange={(e) => setInspecting({ ...inspecting, seo_title: e.target.value })}
                  data-testid="historical-seo-title-input"
                />
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Meta description (search snippet)</label>
                <textarea
                  rows={2}
                  className="input-rf w-full mb-1"
                  placeholder="Short, human-readable 150–160 char summary that appears in Google/Bing search results."
                  maxLength={200}
                  value={inspecting.seo_description || ""}
                  onChange={(e) => setInspecting({ ...inspecting, seo_description: e.target.value })}
                  data-testid="historical-seo-description-input"
                />
                <p className="text-[10px] text-rf-ink-soft mb-3">{(inspecting.seo_description || "").length} / 200 chars</p>
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Hero image alt text (accessibility + image SEO)</label>
                <input
                  type="text"
                  className="input-rf w-full mb-3"
                  placeholder={`e.g. "A rustic bowl of ${inspecting.title.toLowerCase()} on a wooden table"`}
                  maxLength={150}
                  value={inspecting.image_alt || ""}
                  onChange={(e) => setInspecting({ ...inspecting, image_alt: e.target.value })}
                  data-testid="historical-seo-image-alt-input"
                />
                <label className="block text-xs font-black uppercase tracking-widest text-rf-ink-soft mb-1">Keywords (comma-separated, 10–14 terms)</label>
                <textarea
                  rows={2}
                  className="input-rf w-full mb-3"
                  placeholder="medieval recipe, Victorian cooking, historical bread, 14th century pottage, English food history"
                  maxLength={400}
                  value={inspecting.seo_keywords || ""}
                  onChange={(e) => setInspecting({ ...inspecting, seo_keywords: e.target.value })}
                  data-testid="historical-seo-keywords-input"
                />
                <SnippetPreview
                  title={inspecting.seo_title || inspecting.title}
                  description={inspecting.seo_description || ""}
                  url={`https://recipefiler.com/historical/${inspecting.slug || inspecting.historical_id}`}
                  image={inspecting.image_url || null}
                  brand="Recipe Filer"
                  dataTestidPrefix="snippet-preview-historical"
                />
                <div className="h-3" />
                <button
                  type="button"
                  onClick={saveSeoEdits}
                  disabled={inspectorBusy}
                  className="btn-amber inline-flex items-center gap-2 !py-2 !px-4 text-sm disabled:opacity-60"
                  data-testid="historical-seo-save"
                >
                  <ShieldCheck size={16} weight="bold" /> Save SEO overrides
                </button>
              </section>

              {/* HISTORY SUMMARY */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <p className="overline">100-word history summary</p>
                  {inspecting.history_verified ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                      <ShieldCheck size={12} weight="fill" /> Verified — visible to public
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest bg-amber-100 text-amber-900 px-2 py-1 rounded-full">
                      Draft — hidden from public
                    </span>
                  )}
                </div>
                <p className="text-xs text-rf-ink-soft mb-2">
                  Click <b>Draft via AI</b> to have Claude write a 100-word history + cite 3-6 sources. <b>Verify each source against the internet</b> (Wikipedia, Google Books, museum sites), edit the text if needed, then flip &quot;Verified&quot; before it appears on the public page.
                </p>
                <button
                  type="button"
                  onClick={draftHistory}
                  disabled={inspectorBusy}
                  className="btn-outline-ink inline-flex items-center gap-2 !py-2 !px-3 text-sm mb-3 disabled:opacity-60"
                  data-testid="historical-inspector-draft-history"
                >
                  <MagicWand size={16} weight="bold" /> {inspectorBusy ? "Working…" : "Draft via AI"}
                </button>
                <textarea
                  className="input-rf w-full min-h-[160px] text-sm"
                  placeholder="100-word history — factually accurate, evidence-based."
                  value={inspecting.history_summary || ""}
                  onChange={(e) => setInspecting({ ...inspecting, history_summary: e.target.value })}
                  data-testid="historical-inspector-history-textarea"
                />
                <p className="overline mt-3 mb-1">Cited sources</p>
                <div className="space-y-1 mb-3">
                  {(inspecting.history_sources || []).map((s, i) => (
                    <div key={i} className="flex gap-1">
                      <input
                        className="input-rf flex-1 text-xs !py-2"
                        value={s}
                        onChange={(e) => {
                          const next = [...(inspecting.history_sources || [])];
                          next[i] = e.target.value;
                          setInspecting({ ...inspecting, history_sources: next });
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = (inspecting.history_sources || []).filter((_, j) => j !== i);
                          setInspecting({ ...inspecting, history_sources: next });
                        }}
                        className="px-2 text-red-600 hover:bg-red-50 rounded-lg"
                        title="Remove source"
                      >
                        <XIcon size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setInspecting({ ...inspecting, history_sources: [...(inspecting.history_sources || []), ""] })}
                    className="text-xs text-rf-orange font-bold hover:underline"
                  >+ Add a source</button>
                </div>
                <label className="flex items-center gap-2 text-sm text-rf-ink cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!inspecting.history_verified}
                    onChange={(e) => setInspecting({ ...inspecting, history_verified: e.target.checked })}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid="historical-inspector-verified-checkbox"
                  />
                  I&apos;ve verified every source above against the internet — this summary is 100% factually correct.
                </label>
                <div className="flex justify-end mt-4">
                  <button
                    type="button"
                    onClick={saveHistoryEdits}
                    disabled={inspectorBusy}
                    className="btn-amber inline-flex items-center gap-2 text-sm disabled:opacity-60"
                    data-testid="historical-inspector-save-history"
                  >
                    Save history
                  </button>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
